import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * Inline Status Editor Component
 * Allows inline editing of attendance status with dropdown
 */
function AttendanceStatusEditor({ 
  currentStatus, 
  onUpdate, 
  disabled = false 
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(currentStatus);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, width: 0, openUp: false });

  const statuses = [
    { value: 'PRESENT', label: 'Present', color: 'bg-green-100 text-green-800', hoverColor: 'hover:bg-green-200' },
    { value: 'ABSENT', label: 'Absent', color: 'bg-red-100 text-red-800', hoverColor: 'hover:bg-red-200' },
    { value: 'LATE', label: 'Late', color: 'bg-yellow-100 text-yellow-800', hoverColor: 'hover:bg-yellow-200' },
    { value: 'EXCUSED', label: 'Excused', color: 'bg-blue-100 text-blue-800', hoverColor: 'hover:bg-blue-200' },
  ];

  const currentStatusConfig = statuses.find(s => s.value === currentStatus) || statuses[0];

  // Reposition dropdown relative to trigger
  const updateMenuPosition = () => {
    const triggerEl = triggerRef.current;
    if (!triggerEl) return;
    const rect = triggerEl.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    // Heuristic expected menu height
    const expectedMenuHeight = 192; // ~4 items * 48px
    const openUp = spaceBelow < expectedMenuHeight + 12; // prefer opening up when cramped
    setMenuPosition({
      top: openUp ? rect.top - 8 : rect.bottom + 8,
      left: rect.left,
      width: Math.max(rect.width, 140),
      openUp,
    });
  };

  // Close dropdown when clicking outside and keep position on resize/scroll
  useEffect(() => {
    const handleClickOutside = (event) => {
      const menuEl = dropdownRef.current;
      const triggerEl = triggerRef.current;
      if (
        menuEl && !menuEl.contains(event.target) &&
        triggerEl && !triggerEl.contains(event.target)
      ) {
        setIsEditing(false);
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setIsEditing(false);
    };

    const handleRelayout = () => updateMenuPosition();

    if (isEditing) {
      updateMenuPosition();
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleRelayout, true);
      window.addEventListener('resize', handleRelayout);
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('scroll', handleRelayout, true);
        window.removeEventListener('resize', handleRelayout);
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isEditing]);

  const handleStatusChange = async (newStatus) => {
    if (newStatus === currentStatus) {
      setIsEditing(false);
      return;
    }

    setSelectedStatus(newStatus);
    setIsEditing(false);
    
    try {
      await onUpdate(newStatus);
    } catch (error) {
      // Revert on error
      setSelectedStatus(currentStatus);
      console.error('Failed to update status:', error);
    }
  };

  if (disabled) {
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${currentStatusConfig.color}`}>
        {currentStatusConfig.label}
      </span>
    );
  }

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        onClick={() => setIsEditing(!isEditing)}
        className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${currentStatusConfig.color} ${currentStatusConfig.hoverColor} transition-colors cursor-pointer`}
      >
        {currentStatusConfig.label}
        <svg className="ml-1 w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isEditing && createPortal(
        <div
          ref={dropdownRef}
          className="z-[2147483647] bg-white rounded-lg shadow-lg border border-gray-200 py-1"
          style={{
            position: 'fixed',
            top: menuPosition.top,
            left: menuPosition.left,
            minWidth: Math.max(120, menuPosition.width),
            transform: menuPosition.openUp ? 'translateY(-100%)' : 'none',
          }}
          role="menu"
          aria-orientation="vertical"
        >
          {statuses.map((status) => (
            <button
              key={status.value}
              onClick={() => handleStatusChange(status.value)}
              className={`w-full px-3 py-2 text-left text-sm ${
                status.value === selectedStatus ? 'bg-gray-100' : ''
              } hover:bg-gray-50 transition-colors`}
              role="menuitem"
            >
              <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${status.color}`}>
                {status.label}
              </span>
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

export default React.memo(AttendanceStatusEditor);
